import React, { useCallback } from 'react';

import { TouchableOpacityProps, GestureResponderEvent } from 'react-native';

import { Regular } from 'theme/text';
import { Colors } from 'theme/colors';

import { DropdownItemButton, DropdownItemContainer } from './styles';
import { Option } from '../models';
import { FontWeight } from 'theme/fonts';

const DISABLED_OPACITY = 0.5;

/**
 * A touchable dropdown item.
 *
 * @param props - Render props
 */
export function DropdownItem<T>(props: DropdownItemProps<T>) {
  const { disabled, item, selected, onPress } = props;
  const fontWeight = selected ? FontWeight.Bold : FontWeight.Regular;
  const opacity = disabled ? DISABLED_OPACITY : 1;

  /** Emits an `onPress` event with the appropriate item data.
   *
   * @param event - Gesture event emitted from React Native.
   */
  const handleOnPress = useCallback(
    (event: GestureResponderEvent) => {
      if (onPress) {
        onPress(item, event);
      }
    },
    [item, onPress]
  );

  return (
    <DropdownItemContainer>
      <DropdownItemButton
        backgroundColor={selected ? Colors.gallery : Colors.white}
        {...props}
        onPress={handleOnPress}>
        <Regular style={{ opacity }} fontWeight={fontWeight}>
          {item.key}
        </Regular>
      </DropdownItemButton>
    </DropdownItemContainer>
  );
}
/* Type definitions */
interface DropdownItemProps<T> extends Omit<TouchableOpacityProps, 'onPress'> {
  /** Information about the item for the current option */
  item: Option<T>;
  /** A flag that indicates if the current item is selected */
  selected: boolean;
  /**
   * Event handler for `onPress` of the item.
   *
   * @param item - Current item information for this option.
   * @param event - Native event data.
   */
  onPress?(item: Option<T>, event: GestureResponderEvent): void;
}
